import { httpService } from 'src/services/app/http.service'
import { log } from 'src/services/app/log.service'
import { localStorageService } from 'src/services/app/storage.service'
import {
  ANALYTICS_EVENTS,
  API_ERRORS,
  AUTH_ENDPOINTS,
  SUPPORT_ENDPOINTS,
  STORAGE_KEYS,
  USER_ENDPOINTS,
} from 'src/services/consts/api.const'
import { refactorAccountData, refactorSettingsUpdate } from 'src/services/helpers/account.helper'
import { isObjectEmpty } from 'src/services/app/util.service'
import { useAuthStore } from 'stores/auth'

export const accountService = {
  auth0Login,
  loadAccount,
  logout,
  updateSettings,
  submitSupportRequest,
  clearAuth0LoginCache,
  getAuth0LoginCache,
}

/**
 * Clear cached auth0 login data from localStorage
 * Should be called on logout
 */
function clearAuth0LoginCache() {
  localStorageService.remove(STORAGE_KEYS.AUTH0_LOGIN_CACHE)
  log.info('Auth0 login cache cleared')
}

function getAuth0LoginCache() {
  return localStorageService.getCachedData(STORAGE_KEYS.AUTH0_LOGIN_CACHE)
}

async function auth0Login() {
  try {
    const res = await httpService.post(AUTH_ENDPOINTS.AUTH0_LOGIN)
    const accountData = {
      accountId: res.account_id,
      servers: res.servers || {},
    }

    // Validate that account data exists
    if (!accountData.accountId) {
      throw new Error('Failed to login')
    }

    // Validate that servers exist
    if (!accountData.servers || Object.keys(accountData.servers).length === 0) {
      throw new Error('No servers configured for this account')
    }

    if (!getAuth0LoginCache()) {
      userLoginSync({ accountId: accountData.accountId })
    }

    // Cache the auth0 login data (shared across all tabs)
    localStorageService.cacheData(
      STORAGE_KEYS.AUTH0_LOGIN_CACHE,
      accountData,
      1000 * 60 * 60 * 24 * 7
    ) // 7 days
    return accountData
  } catch (error) {
    // Only 401 and AUTH_EXPIRED are handled by http.service (_handleAuthError).
    // Every other bootstrap failure (5xx, network, 200+error body, missing
    // accountId/servers) leaves Auth0 authenticated without account context -
    // force logout so the user can recover via a fresh SSO attempt.
    log.error('auth0Login failed', error)
    const isInterceptorAuthLogout =
      error?.name === API_ERRORS.AUTH_EXPIRED || error?.response?.status === 401
    if (!isInterceptorAuthLogout) {
      const authStore = useAuthStore()
      await authStore.logout({
        authError: true,
        event: ANALYTICS_EVENTS.SIGNED_OUT_SIGN_IN_ERROR,
        details: {
          step: 'account_bootstrap',
          code: error?.response?.status || error?.code || error?.name,
        },
      })
    }
    throw error
  }
}

async function loadAccount() {
  const data = await httpService.post(AUTH_ENDPOINTS.LOAD_ACCOUNT)
  if (isObjectEmpty(data)) {
    throw new Error('No account data found')
  }
  const refactoredData = refactorAccountData(data)

  log.info('Account data loaded', refactoredData)
  return refactoredData
}

async function userLoginSync({ accountId }) {
  try {
    await httpService.post(USER_ENDPOINTS.LOGIN_SYNC, {
      account_id: accountId,
    })
  } catch (error) {
    log.error('userLoginSync failed', error)
  }
}

async function logout() {
  try {
    await httpService.post(AUTH_ENDPOINTS.LOGOUT)
    log.info('Backend logout successful')
  } catch (error) {
    log.error('Backend logout failed', error)
    // Don't throw - we still want to proceed with Auth0 logout even if backend logout fails
  }
}

/**
 * Persists a partial settings update. `account_id` is auto-injected by the http.service request
 * interceptor - do not pass it manually. The route path is still `/user/update_preferences`.
 * @param {Object} settings - Partial camelCase settings, e.g. { theme: 'dark' } or
 *   { showOnboardingTour: false }
 * @returns {Object} Settings fields present on the response (`theme`, `showOnboardingTour`)
 */
async function updateSettings({ theme, showOnboardingTour } = {}) {
  const data = await httpService.post(USER_ENDPOINTS.UPDATE_SETTINGS, {
    update_info: {
      theme,
      show_onboarding_tour: showOnboardingTour,
    },
  })
  return refactorSettingsUpdate(data.settings || {})
}

async function submitSupportRequest({ topic, message }) {
  const payload = {
    topic,
    problem_description: message,
  }

  log.info('[Support] Submitting support request', payload)

  const res = await httpService.post(SUPPORT_ENDPOINTS.NEW_TICKET, payload)

  log.info('[Support] Support request submitted successfully', res)
  return res
}
